import { ItemCard } from "./ItemCard";
import { CyItem } from "./CyItem";
import { Col, Row, Card, CardBody, CardHeader, Alert } from "reactstrap";
import { CSSProperties, useState } from "react";
import { graphql } from "../../gql";
import { TextareaInputGraphQlMutate } from "./TextareaInputGraphQlMutate";
import { useTranslation, Trans } from "react-i18next";
import { LyricsPart, getLyricsParts } from "../../chordsheet/LyricsExtractor";
import { getLyricsPartLabel } from "./LyricsPartLabel";
import { SongUb } from "../../gql/graphql";
import { useAsyncEffect } from "../../common/hooks/useAsyncEffect";

interface Props {
  song: SongUb;
  isDisabled?: boolean;
}

const MONO_FONT_FAMILY =
  "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'SF Mono', Menlo, Monaco, 'Courier New', monospace";
const TEXTAREA_WIDTH_CH = 35;
const LINE_HEIGHT = 1.5;
const MAX_PREVIEW_LINES = 8;

const updateChordsheetMutation = graphql(`
  mutation updateChordsheet($bucket: String!, $id: ID!, $chordsheet: String) {
    updateChordsheet(bucket: $bucket, id: $id, chordsheet: $chordsheet) {
      ...SongFields
    }
  }
`);

export function ChordsheetCard({ song, isDisabled = false }: Props) {
  const { t } = useTranslation();
  const [textareaContentPreview, setTextareaContentPreview] = useState("");
  const [lyricsOnlySong, setLyricsOnlySong] = useState<LyricsPart[]>([]);
  const [error, setError] = useState<{
    message: string;
    lineNumber?: number;
  } | null>(null);

  useAsyncEffect(
    (isActive) => {
      if (!textareaContentPreview) {
        setLyricsOnlySong([]);
        setError(null);
        return;
      }

      getLyricsParts(textareaContentPreview)
        .then((result) => {
          if (isActive()) {
            if (Array.isArray(result)) {
              setLyricsOnlySong(result);
              setError(null);
            } else {
              setError({
                message: result.message,
                lineNumber: result.lineNumber,
              });
              setLyricsOnlySong([]);
            }
          }
        })
        .catch((err) => {
          if (isActive()) {
            setError({ message: err.message || "Failed to parse chordsheet" });
            setLyricsOnlySong([]);
          }
        });
    },
    [textareaContentPreview],
  );

  return (
    <ItemCard
      title={t("items.sourceFiles.chordsheetTitle")}
      dataCy={CyItem.ChordsheetCard}
      helpCollapsedArea={
        <>
          <ul>
            <li>
              {t("items.chordsheet.lyrics")}
              <ul>
                <li>{t("items.chordsheet.previewDescription")}</li>
                <li>
                  {t("items.chordsheet.optimalFormat", {
                    width: TEXTAREA_WIDTH_CH,
                    lines: MAX_PREVIEW_LINES,
                  })}
                </li>
              </ul>
            </li>
            <li>{t("items.chordsheet.chordsheetFeatureHint")}</li>
          </ul>
        </>
      }
    >
      {error && (
        <Alert color="danger" id="chordsheet-error" className="mb-2">
          <Trans
            i18nKey="items.chordsheet.parseError"
            components={{
              chordproLink: (
                <a
                  href="https://www.chordpro.org/chordpro/chordpro-introduction/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="alert-link"
                />
              ),
            }}
          />
          {error.lineNumber !== undefined &&
            `: ${t("items.chordsheet.line", { lineNumber: error.lineNumber })}`}
        </Alert>
      )}
      <Row className="g-2">
        <Col xs={12} md="auto">
          <TextareaInputGraphQlMutate
            htmlTagIdAndName={"chordsheet"}
            value={song.chordsheet}
            mutationDocument={updateChordsheetMutation}
            buildVariables={(validatedValue) => ({
              id: song.id,
              chordsheet: validatedValue as string,
              bucket: song.bucket,
            })}
            style={
              {
                minHeight: "100px",
                width: `calc(${TEXTAREA_WIDTH_CH}ch + 1.5rem + 2px)`, // 35ch + padding (p-2 = 0.75rem left + 0.75rem right) + border (2px)
                resize: "vertical",
                fieldSizing: "content",
                fontFamily: MONO_FONT_FAMILY,
              } as CSSProperties
            }
            setContentPreview={(v) => setTextareaContentPreview(v ?? "")}
            isDisabled={isDisabled}
          />
        </Col>
        <Col xs={12} md="auto">
          {lyricsOnlySong.map((part, index) => {
            const label = getLyricsPartLabel(part);
            return (
              <Card key={index} className="mb-2 bg-dark text-white">
                <CardHeader className="p-2 small fw-bold">{label}</CardHeader>
                <CardBody
                  style={{
                    width: `calc(${TEXTAREA_WIDTH_CH}ch + 1rem)`, // 35ch + padding (p-2 = 0.5rem left + 0.5rem right)
                    fontFamily: MONO_FONT_FAMILY,
                    whiteSpace: "pre-line",
                    lineHeight: LINE_HEIGHT,
                    maxHeight: `calc(${LINE_HEIGHT}em * ${MAX_PREVIEW_LINES} + 1rem)`, // 8 lines + padding
                    overflow: "auto",
                  }}
                  className="p-2"
                  role="textbox"
                >
                  {part.content.trim()}
                </CardBody>
              </Card>
            );
          })}
        </Col>
      </Row>
    </ItemCard>
  );
}
